import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 p-3 rounded-lg shadow-xl">
      <p className="text-xs text-zinc-400 mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="font-mono text-zinc-200">{p.value?.toFixed(1)}</span>
        </div>
      ))}
    </div>
  );
};

export default function InventoryPage({ results, analysis }) {
  const inv = results?.inventory || [];

  if (inv.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="inventory-page">
        Import data to view inventory analysis
      </div>
    );
  }

  const chartData = inv.map(i => ({
    name: i.label,
    Turnover: i.turnover,
    'Days Outstanding': i.days_outstanding,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Inventory Turnover Analysis</h2>
          <p className="text-sm text-zinc-400 mt-1">Working capital efficiency and inventory management assessment</p>
        </div>
        <SectionPdfExport
          title="Inventory Turnover"
          analysisName={analysis?.name}
          rows={inv}
          columns={buildColumns([
            { label: 'Scenario', key: 'label' },
            { label: 'COGS', key: 'cogs', format: 'currency' },
            { label: 'Inventory', key: 'inventory', format: 'currency' },
            { label: 'Turnover', key: 'turnover', format: 'number' },
            { label: 'Days Outstanding', key: 'days_outstanding', format: 'number' },
          ])}
        />
      </div>

      {/* Chart */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Turnover Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
              <Bar dataKey="Turnover" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Days Outstanding" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-xs">Scenario</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">COGS</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Avg Inventory</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Turnover</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Days Outstanding</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Assessment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.map((row, i) => {
                const isGood = row.turnover >= 4;
                return (
                  <TableRow key={i} className="border-zinc-800/40 hover:bg-zinc-800/20">
                    <TableCell className="text-xs text-zinc-300 font-medium">{row.label}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(row.cogs)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(row.inventory)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-200">{row.turnover?.toFixed(2)}x</TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-200">{row.days_outstanding?.toFixed(1)} days</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={`text-[10px] ${isGood ? 'border-emerald-500/30 text-emerald-400' : 'border-amber-500/30 text-amber-400'}`}>
                        {isGood ? 'Efficient' : 'Monitor'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardContent className="p-4 text-xs text-zinc-400">
          <p><span className="text-zinc-200 font-medium">Retail industry benchmark:</span> Average inventory turnover ~4-8x (varies by segment). Higher turnover indicates efficient inventory management. Single point estimate used — no beginning/ending inventory data available.</p>
        </CardContent>
      </Card>
    </div>
  );
}
